/**
 * Dashboard Generation Page.
 * Allows users to generate and create dashboards from selected data.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button, Text } from '@fluentui/react-components';
import { DocumentSearchRegular, ArrowResetRegular } from '@fluentui/react-icons';
import type { ChartPlan, DashboardPlan } from '@excel-ai-studio/shared';
import { generateDashboardPlan } from '../services/api.client';
import { readDashboardSelection, type SelectionInfo } from '../office';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { DashboardPreviewPanel } from '../components/features/DashboardPreviewPanel';
import './DashboardPage.css';

export const DashboardPage: React.FC = () => {
  type DashboardLayoutMode = 'compact' | 'executive';

  // Selection
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [selectionLoading, setSelectionLoading] = useState(false);

  // Plan generation
  const [plan, setPlan] = useState<DashboardPlan>();
  const [planLoading, setPlanLoading] = useState(false);

  // Dashboard creation
  const [creationLoading, setCreationLoading] = useState(false);
  const [selectedKpiIds, setSelectedKpiIds] = useState<string[]>([]);
  const [selectedChartIds, setSelectedChartIds] = useState<string[]>([]);
  const [layoutMode, setLayoutMode] = useState<DashboardLayoutMode>('executive');

  // Shared
  const [error, setError] = useState<string>();

  // ─── Helpers ─────────────────────────────────────────

  const resetAll = () => {
    setSelectionInfo(undefined);
    setPlan(undefined);
    setError(undefined);
    setSelectedKpiIds([]);
    setSelectedChartIds([]);
    setLayoutMode('executive');
  };

  const isSelectionEmpty = (values: unknown[][]): boolean =>
    values.every((row) =>
      row.every((cell) => cell === null || cell === undefined || String(cell).trim() === ''),
    );

  // ─── Step 1: Read selection ──────────────────────────

  const handleReadSelection = async () => {
    setError(undefined);
    setPlan(undefined);
    setSelectionLoading(true);

    try {
      const info = await readDashboardSelection();

      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule sélectionnée. Sélectionnez des données dans Excel puis réessayez.');
        setSelectionInfo(undefined);
        return;
      }

      if (isSelectionEmpty(info.values)) {
        setError('La sélection ne contient que des cellules vides.');
        setSelectionInfo(undefined);
        return;
      }

      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
      setSelectionInfo(undefined);
    } finally {
      setSelectionLoading(false);
    }
  };

  // ─── Step 2: Generate plan ──────────────────────────

  const handleGeneratePlan = useCallback(async () => {
    if (!selectionInfo) return;

    setError(undefined);
    setPlanLoading(true);

    try {
      if (selectionInfo.rowCount < 2 || selectionInfo.colCount < 2) {
        throw new Error(
          'Sélection insuffisante pour un dashboard. Sélectionnez un tableau (minimum 2 lignes et 2 colonnes), puis relancez.',
        );
      }

      const normalized = normalizeSelectionForDashboard(selectionInfo.values);
      const normalizedValues: unknown[][] = [normalized.headers, ...normalized.rows];

      const response = await generateDashboardPlan({
        values: normalizedValues,
        headers: normalized.headers,
      });

      setPlan(ensurePlanFallbacks(response.plan, normalizedValues, normalized.headers));
    } catch (err) {
      setError((err as Error).message);
      setPlan(undefined);
    } finally {
      setPlanLoading(false);
    }
  }, [selectionInfo]);

  useEffect(() => {
    if (!plan) {
      setSelectedKpiIds([]);
      setSelectedChartIds([]);
      return;
    }
    setSelectedKpiIds(plan.kpis.map((k) => k.id));
    setSelectedChartIds(plan.charts.map((c) => c.id));
  }, [plan]);

  // ─── Step 3: Create dashboard ───────────────────────

  const handleCreateDashboard = useCallback(async () => {
    if (!plan || !selectionInfo) return;

    setError(undefined);
    setCreationLoading(true);

    try {
      const { createDashboardSheet } = await import('../services/dashboard-creator.service');
      const normalized = normalizeSelectionForDashboard(selectionInfo.values);
      const normalizedValues: unknown[][] = [normalized.headers, ...normalized.rows];
      const effectivePlan: DashboardPlan = {
        ...plan,
        kpis: plan.kpis.filter((k) => selectedKpiIds.includes(k.id)),
        charts: plan.charts.filter((c) => selectedChartIds.includes(c.id)),
      };

      const result = await createDashboardSheet(effectivePlan, {
        values: normalizedValues,
        headers: normalized.headers,
        sheetName: effectivePlan.title,
        layoutMode,
        sourceRangeAddress: selectionInfo.address,
      });

      if (result.success) {
        setError(undefined);
        // Reset after successful creation
        setTimeout(() => {
          resetAll();
        }, 1000);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreationLoading(false);
    }
  }, [layoutMode, plan, selectionInfo, selectedChartIds, selectedKpiIds]);

  const toggleKpi = (id: string) => {
    setSelectedKpiIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleChart = (id: string) => {
    setSelectedChartIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllKpis = () => {
    if (!plan) return;
    setSelectedKpiIds(plan.kpis.map((k) => k.id));
  };

  const clearAllKpis = () => setSelectedKpiIds([]);

  const selectAllCharts = () => {
    if (!plan) return;
    setSelectedChartIds(plan.charts.map((c) => c.id));
  };

  const clearAllCharts = () => setSelectedChartIds([]);

  // ─── Render ──────────────────────────────────────────

  return (
    <div className="dashboard-page">
      {/* Error Display */}
      {error && <ErrorDisplay message={error} />}

      {/* Step 1: Read Selection */}
      {!selectionInfo && !plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 1: Sélectionner les données
          </Text>
          <Text size={300}>
            Sélectionnez un tableau de données dans Excel pour générer un dashboard automatique.
          </Text>
          <Button
            appearance="primary"
            size="large"
            icon={<DocumentSearchRegular />}
            onClick={handleReadSelection}
            disabled={selectionLoading}
          >
            {selectionLoading ? 'Lecture en cours...' : 'Lire la sélection'}
          </Button>
        </div>
      )}

      {/* Step 2: Generate Plan */}
      {selectionInfo && !plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 2: Générer le plan du dashboard
          </Text>
          <Text size={300}>
            {selectionInfo.values.length} lignes × {selectionInfo.values[0]?.length || 0} colonnes détectées
          </Text>
          <div className="button-group">
            <Button
              appearance="primary"
              size="large"
              onClick={handleGeneratePlan}
              disabled={planLoading}
            >
              {planLoading ? 'Génération en cours...' : 'Générer le plan'}
            </Button>
            <Button
              appearance="secondary"
              size="large"
              icon={<ArrowResetRegular />}
              onClick={resetAll}
            >
              Recommencer
            </Button>
          </div>
          {planLoading && <LoadingSpinner />}
        </div>
      )}

      {/* Step 3: Preview and Create */}
      {plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 3: Aperçu et création
          </Text>
          <Text size={300}>
            L’analyse de la sélection est terminée. Choisissez les KPI et graphiques à inclure.
          </Text>

          <div style={{ margin: '12px 0', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <Text weight="semibold" size={300}>KPI proposés</Text>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="small" appearance="subtle" onClick={selectAllKpis} disabled={creationLoading || plan.kpis.length === 0}>
                  Tout cocher
                </Button>
                <Button size="small" appearance="subtle" onClick={clearAllKpis} disabled={creationLoading || selectedKpiIds.length === 0}>
                  Tout décocher
                </Button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {plan.kpis.length === 0 && <Text size={200}>Aucun KPI proposé.</Text>}
              {plan.kpis.map((kpi) => (
                <label key={kpi.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selectedKpiIds.includes(kpi.id)}
                    onChange={() => toggleKpi(kpi.id)}
                    disabled={creationLoading}
                  />
                  <span>{kpi.title} ({kpi.calculation})</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ margin: '12px 0', padding: '10px', border: '1px solid #ddd', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <Text weight="semibold" size={300}>Graphiques proposés</Text>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="small" appearance="subtle" onClick={selectAllCharts} disabled={creationLoading || plan.charts.length === 0}>
                  Tout cocher
                </Button>
                <Button size="small" appearance="subtle" onClick={clearAllCharts} disabled={creationLoading || selectedChartIds.length === 0}>
                  Tout décocher
                </Button>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              <Text size={200} style={{ color: '#605e5c' }}>
                Cochez un ou plusieurs graphiques à inclure dans la feuille Dashboard.
              </Text>
              {plan.charts.length === 0 && <Text size={200}>Aucun graphique proposé.</Text>}
              {plan.charts.map((chart) => (
                <label key={chart.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selectedChartIds.includes(chart.id)}
                    onChange={() => toggleChart(chart.id)}
                    disabled={creationLoading}
                  />
                  <span>{chart.title} ({chart.type})</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ margin: '8px 0 12px', padding: '8px 10px', background: '#f6f8fa', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
              <Text size={200}>Mode visuel du dashboard</Text>
              <select
                value={layoutMode}
                onChange={(e) => setLayoutMode(e.target.value as DashboardLayoutMode)}
                disabled={creationLoading}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1', minWidth: 170 }}
              >
                <option value="executive">Executive (présentation)</option>
                <option value="compact">Compact (plus dense)</option>
              </select>
            </div>
            <Text size={200}>
              Sélection actuelle: <strong>{selectedKpiIds.length}</strong> KPI et <strong>{selectedChartIds.length}</strong> graphique(s)
            </Text>
          </div>

          <DashboardPreviewPanel
            plan={{
              ...plan,
              kpis: plan.kpis.filter((k) => selectedKpiIds.includes(k.id)),
              charts: plan.charts.filter((c) => selectedChartIds.includes(c.id)),
            }}
            onGenerate={handleCreateDashboard}
            isGenerating={creationLoading}
          />
          <Button
            appearance="secondary"
            size="large"
            icon={<ArrowResetRegular />}
            onClick={resetAll}
            disabled={creationLoading}
          >
            Recommencer
          </Button>
        </div>
      )}
    </div>
  );
};

function resolveColumnRef(raw: string, headers: readonly string[]): string | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return null;
  const norm = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  const normalizedHeaders = headers.map((h) => norm(String(h ?? '')));
  const target = norm(trimmed);
  const idxExact = normalizedHeaders.findIndex((h) => h === target);
  if (idxExact >= 0) return String(headers[idxExact] ?? '').trim() || null;

  const colLabel = /^colonne\s*(\d+)$/i.exec(trimmed);
  if (colLabel) {
    const i = parseInt(colLabel[1], 10) - 1;
    if (i >= 0 && i < headers.length) return String(headers[i] ?? '').trim() || null;
  }
  const digits = /^(\d+)$/.exec(trimmed);
  if (digits) {
    const n = parseInt(digits[1], 10);
    const asZero = n > 0 && n <= headers.length ? n - 1 : -1;
    const asIdx = n >= 0 && n < headers.length ? n : -1;
    const pick = asZero >= 0 ? asZero : asIdx;
    if (pick >= 0) return String(headers[pick] ?? '').trim() || null;
  }
  return null;
}

function ensurePlanFallbacks(plan: DashboardPlan, values: unknown[][], normalizedHeaders?: string[]): DashboardPlan {
  const derived = normalizeSelectionForDashboard(values);
  const headers = (normalizedHeaders && normalizedHeaders.length > 0 ? normalizedHeaders : derived.headers).map((h) => String(h ?? '').trim());
  const rows = derived.rows;

  const toNumber = (v: unknown): number | null => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const parsed = Number(v.replace(/\s/g, '').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const numericHeader = headers.find((h, idx) => {
    const sample = rows.slice(0, 50);
    let seen = 0;
    let numeric = 0;
    for (const row of sample) {
      const value = (row as unknown[])[idx];
      if (value === null || value === undefined || String(value).trim() === '') continue;
      seen += 1;
      if (toNumber(value) !== null) numeric += 1;
    }
    return seen > 0 && numeric / seen >= 0.6;
  });

  const normalize = (v: string) =>
    String(v ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  const normalizedHeaderSet = new Set(headers.map(normalize));
  const isGenericColumn = (v: string) => /^colonne\s*\d+$/i.test(String(v ?? '').trim());

  const stockCurrentHeader = headers.find((h) => /(stock.*courant|qte.*stock|quantite.*stock)/i.test(normalize(h)));
  const stockMinHeader = headers.find((h) => /(stock.*min|minimal|mini)/i.test(normalize(h)));
  const stockMaxHeader = headers.find((h) => /(stock.*max|maximal|maxi)/i.test(normalize(h)));
  const amountHeader =
    headers.find((h) => /(montant|valeur|prix|pu.*achat|achat)/i.test(normalize(h)))
    ?? numericHeader;

  const normalizedPlanKpis = (plan.kpis ?? [])
    .map((kpi) => {
      const kpiTitle = String(kpi.title ?? '').trim();
      const kpiColumnOriginal = String(kpi.column ?? '').trim();
      const lowerTitle = normalize(kpiTitle);
      const looksLikeRowCount =
        lowerTitle.includes('nombre de lign') ||
        lowerTitle.includes('nombre lign') ||
        (kpi.calculation === 'count' &&
          (isGenericColumn(kpiColumnOriginal) || /^(\d+)$/.test(kpiColumnOriginal)));

      let kpiColumnRaw = kpiColumnOriginal;
      if (!looksLikeRowCount && kpiColumnOriginal !== '__row_count__') {
        const resolved = resolveColumnRef(kpiColumnOriginal, headers);
        if (resolved) kpiColumnRaw = resolved;
      }
      if (looksLikeRowCount) {
        return { ...kpi, title: 'Nombre de lignes', column: '__row_count__' };
      }
      let title = kpi.title;
      if (kpiColumnRaw && kpiColumnRaw !== '__row_count__' && /colonne\s*\d+/i.test(kpiColumnOriginal)) {
        title = kpiTitle.replace(/colonne\s*\d+/i, kpiColumnRaw).replace(/\s+/g, ' ').trim();
      }
      return { ...kpi, title, column: kpiColumnRaw };
    })
    .filter((kpi) => {
      const col = String(kpi.column ?? '').trim();
      if (col === '__row_count__') return true;
      if (!col || isGenericColumn(col)) return false;
      return normalizedHeaderSet.has(normalize(col));
    });

  const autoDomainKpis: DashboardPlan['kpis'] = [
    {
      id: 'fallback-row-count-ui',
      title: 'Nombre de lignes',
      column: '__row_count__',
      calculation: 'count' as const,
      priority: 'high' as const,
    },
    ...(stockCurrentHeader
      ? [{
          id: 'fallback-stock-current-sum-ui',
          title: `Total ${stockCurrentHeader}`,
          column: stockCurrentHeader,
          calculation: 'sum' as const,
          priority: 'high' as const,
        }]
      : []),
    ...(stockMinHeader
      ? [{
          id: 'fallback-stock-min-ui',
          title: `Minimum ${stockMinHeader}`,
          column: stockMinHeader,
          calculation: 'min' as const,
          priority: 'medium' as const,
        }]
      : []),
    ...(stockMaxHeader
      ? [{
          id: 'fallback-stock-max-ui',
          title: `Maximum ${stockMaxHeader}`,
          column: stockMaxHeader,
          calculation: 'max' as const,
          priority: 'medium' as const,
        }]
      : []),
    ...(amountHeader
      ? [{
          id: 'fallback-amount-sum-ui',
          title: `Total ${amountHeader}`,
          column: amountHeader,
          calculation: 'sum' as const,
          priority: 'medium' as const,
        }]
      : []),
  ];

  const safeKpis: DashboardPlan['kpis'] = normalizedPlanKpis.length > 0
    ? normalizedPlanKpis
    : [
        {
          id: 'fallback-row-count-ui',
          title: 'Nombre de lignes',
          column: '__row_count__',
          calculation: 'count' as const,
          priority: 'high' as const,
        },
        ...(numericHeader
          ? [{
              id: 'fallback-sum-ui',
              title: `Total ${numericHeader}`,
              column: numericHeader,
              calculation: 'sum' as const,
              priority: 'medium' as const,
            }]
          : []),
      ];
  const mergedKpis = [...safeKpis];
  for (const fallbackKpi of autoDomainKpis) {
    if (!mergedKpis.some((k) => k.id === fallbackKpi.id || normalize(k.title) === normalize(fallbackKpi.title))) {
      mergedKpis.push(fallbackKpi);
    }
  }

  const preferredCategory =
    headers.find((h) => /(designation|article|fournisseur|nom|reference|emplacement)/i.test(normalize(h)))
    ?? headers.find((h, idx) => {
      const sample = rows.slice(0, 50);
      let seen = 0;
      let numeric = 0;
      for (const row of sample) {
        const value = (row as unknown[])[idx];
        if (value === null || value === undefined || String(value).trim() === '') continue;
        seen += 1;
        if (toNumber(value) !== null) numeric += 1;
      }
      return seen > 0 && numeric / seen < 0.4;
    })
    ?? headers[0];

  const normalizedPlanCharts = (plan.charts ?? [])
    .map((chart) => {
      const rx = resolveColumnRef(String(chart.xColumn ?? ''), headers);
      const ry = resolveColumnRef(String(chart.yColumn ?? ''), headers);
      if (!rx || !ry) return null;
      return { ...chart, xColumn: rx, yColumn: ry };
    })
    .filter((chart): chart is NonNullable<typeof chart> => {
      if (!chart) return false;
      const x = String(chart.xColumn ?? '').trim();
      const y = String(chart.yColumn ?? '').trim();
      if (!x || !y || isGenericColumn(x) || isGenericColumn(y)) return false;
      if (!normalizedHeaderSet.has(normalize(x)) || !normalizedHeaderSet.has(normalize(y))) return false;
      if (normalize(x) === normalize(y)) return false;
      return true;
    });

  const primaryMetric = stockCurrentHeader ?? amountHeader ?? numericHeader ?? '';

  const isTextLikeColumn = (idx: number): boolean => {
    const sample = rows.slice(0, 80);
    let seen = 0;
    let numeric = 0;
    for (const row of sample) {
      const value = (row as unknown[])[idx];
      if (value === null || value === undefined || String(value).trim() === '') continue;
      seen += 1;
      if (toNumber(value) !== null) numeric += 1;
    }
    return seen > 0 && numeric / seen < 0.45;
  };

  const textLikeHeaders = headers.filter((h, idx) => isTextLikeColumn(idx));

  const secondCategory =
    textLikeHeaders.find(
      (h) => normalize(h) !== normalize(preferredCategory) && /fournisseur|supplier|client/i.test(normalize(h)),
    )
    ?? textLikeHeaders.find(
      (h) => normalize(h) !== normalize(preferredCategory) && /designation|article|emplacement/i.test(normalize(h)),
    )
    ?? textLikeHeaders.find((h) => normalize(h) !== normalize(preferredCategory));

  const buildFallbackChartCandidates = (): ChartPlan[] => {
    const out: ChartPlan[] = [];
    if (preferredCategory && primaryMetric) {
      out.push({
        id: 'fallback-chart-ui',
        title: `${primaryMetric} par ${preferredCategory}`,
        type: 'bar' as const,
        xColumn: preferredCategory,
        yColumn: primaryMetric,
        priority: 'high' as const,
      });
    }
    if (secondCategory && primaryMetric && normalize(secondCategory) !== normalize(preferredCategory)) {
      out.push({
        id: 'fallback-chart-2-ui',
        title: `${primaryMetric} par ${secondCategory}`,
        type: 'bar' as const,
        xColumn: secondCategory,
        yColumn: primaryMetric,
        priority: 'medium' as const,
      });
    } else if (
      preferredCategory
      && amountHeader
      && stockCurrentHeader
      && normalize(amountHeader) !== normalize(stockCurrentHeader)
      && normalize(primaryMetric) === normalize(stockCurrentHeader)
    ) {
      out.push({
        id: 'fallback-chart-2-ui',
        title: `${amountHeader} par ${preferredCategory}`,
        type: 'bar' as const,
        xColumn: preferredCategory,
        yColumn: amountHeader,
        priority: 'medium' as const,
      });
    }
    return out;
  };

  const chartFingerprint = (c: { readonly xColumn: string; readonly yColumn: string; readonly type: string }) =>
    `${normalize(c.xColumn)}|${normalize(c.yColumn)}|${String(c.type)}`;

  let safeCharts: ChartPlan[] = [...normalizedPlanCharts];
  const seenChartKeys = new Set(safeCharts.map(chartFingerprint));
  const MAX_SUGGESTED_CHARTS = 4;
  for (const candidate of buildFallbackChartCandidates()) {
    if (safeCharts.length >= MAX_SUGGESTED_CHARTS) break;
    const fp = chartFingerprint(candidate);
    if (!seenChartKeys.has(fp)) {
      safeCharts.push(candidate);
      seenChartKeys.add(fp);
    }
  }
  const finalKpis = mergedKpis.slice(0, 6);
  const finalCharts = safeCharts;
  return {
    ...plan,
    kpis: finalKpis,
    charts: finalCharts,
    rowCount: rows.length,
    columnCount: headers.length,
    description:
      `Prévisualisation : ${finalKpis.length} indicateur(s) et ${finalCharts.length} graphique(s) pour ${rows.length} ligne(s) de données (${headers.length} colonnes, en-têtes détectées).`,
  };
}

function normalizeSelectionForDashboard(values: unknown[][]): { headers: string[]; rows: unknown[][] } {
  if (!Array.isArray(values) || values.length === 0) {
    return { headers: [], rows: [] };
  }
  const firstRow = (values[0] as unknown[]).map((v) => String(v ?? '').trim());
  const colCount = firstRow.length;
  const normalize = (v: string) =>
    v
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  const toNumber = (v: unknown): number | null => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const parsed = Number(v.replace(/\s/g, '').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };
  const maxCandidateRow = Math.min(values.length - 1, 4);
  let bestRowIndex = -1;
  let bestScore = -Infinity;
  for (let candidate = 0; candidate <= maxCandidateRow; candidate++) {
    const row = (values[candidate] as unknown[]).map((v) => String(v ?? '').trim());
    const nonEmpty = row.filter((h) => h !== '').length;
    if (nonEmpty === 0) continue;
    const unique = new Set(row.map((h) => h.toLowerCase())).size;
    const numericLike = row.filter((h) => /^-?\d+([.,]\d+)?$/.test(h)).length;
    const businessHints = row.filter((h) =>
      /(stock|fourn|article|designation|unite|emplacement|prix|achat|min|max|courant|nom|date|code|reference)/i.test(
        normalize(h),
      ),
    ).length;

    const sampleRows = values.slice(candidate + 1, Math.min(values.length, candidate + 20));
    let transitionCount = 0;
    for (let idx = 0; idx < colCount; idx++) {
      const cell = row[idx];
      if (!cell) continue;
      const cellIsNumeric = toNumber(cell) !== null;
      let seen = 0;
      let numeric = 0;
      for (const sample of sampleRows) {
        const value = (sample as unknown[])[idx];
        if (value === null || value === undefined || String(value).trim() === '') continue;
        seen += 1;
        if (toNumber(value) !== null) numeric += 1;
      }
      if (!cellIsNumeric && seen > 0 && numeric / seen >= 0.7) transitionCount += 1;
    }

    const score =
      nonEmpty * 2 +
      unique * 1.2 +
      businessHints * 3 +
      transitionCount * 2.5 -
      numericLike * 3 -
      candidate * 0.5;
    if (score > bestScore) {
      bestScore = score;
      bestRowIndex = candidate;
    }
  }

  if (bestRowIndex >= 0 && bestScore >= 6) {
    const rawHeaders = (values[bestRowIndex] as unknown[]).map((v) => String(v ?? '').trim());
    const sanitizedHeaders = rawHeaders.map((h, idx) =>
      h && !/^-?\d+([.,]\d+)?$/.test(h) ? h : `Colonne ${idx + 1}`,
    );
    const rows = values.slice(bestRowIndex + 1);
    return { headers: sanitizedHeaders, rows };
  }

  const syntheticHeaders = Array.from({ length: colCount }, (_, i) => `Colonne ${i + 1}`);
  return { headers: syntheticHeaders, rows: values };
}
